import React, { useState } from 'react';
import { LayoutHeader, LayoutFooter } from '@/components/layout';
import { Tabs, WorkspaceInstance, ResetModal } from '@/components/workspace';
import { motion, AnimatePresence } from 'motion/react';
import { CustomToaster } from '@/components/ui/Toast';
import { ArrowLeft, Plus } from 'lucide-react';
import { AnimatedFileSearch } from '@/components/common/AnimatedFileSearch';
import { AnimatedCompass } from '@/components/common/AnimatedCompass';
import { useSession } from '@/hooks/useSession';
import { useWebhook } from '@/hooks/useWebhook';
import { useWorkspacesState } from '@/hooks/useWorkspacesState';
import { JobImportModal, JobImportPayload } from '@/components/common/JobImportModal';
import { INITIAL_TARGET_DETAILS } from '@/constants/app';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';

export default function AppPage() {
  const { recordUsage } = useSession();
  const { fireWebhook } = useWebhook();
  
  const {
    workspaces,
    appMode,
    activeIds,
    handleSelectMode,
    handleAddWorkspace,
    handleCloseWorkspace,
    handleRenameWorkspace,
    handleGoHome,
    resetWorkspaceContent,
    importJob,
    importJobToExisting,
    persistActiveIds
  } = useWorkspacesState();

  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [pendingJobData, setPendingJobData] = useState<any>(null);
  const [extensionJob, setExtensionJob] = useState<JobImportPayload | null>(null);

  React.useEffect(() => {
    // 1. Listen for postMessage from newer extension
    const handler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'JOB_FROM_EXTENSION' && event.data?.payload) {
        setExtensionJob(event.data.payload);
      }
    };
    window.addEventListener('message', handler);

    // 2. Fallback: Parse URL params from older extension implementation
    const params = new URLSearchParams(window.location.search);
    const incomingJobText = params.get('jobText');
    const incomingCompany = params.get('company');
    const incomingPosition = params.get('position');
    const incomingLocation = params.get('location');

    if (incomingJobText || incomingCompany || incomingPosition || incomingLocation) {
      setExtensionJob({
        jobTitle: incomingPosition || '',
        company: incomingCompany || '',
        location: incomingLocation || '',
        description: incomingJobText || '',
        source: 'extension', // or 'url'
        url: window.location.href
      });
      // Clear URL so it doesn't re-trigger on refresh
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    return () => window.removeEventListener('message', handler);
  }, []);

  const handleTailorFromExtension = (job: JobImportPayload) => {
    const newData = {
      jobText: (job.description || '').substring(0, 15000),
      jobLink: job.url || '',
      targetDetails: {
        ...INITIAL_TARGET_DETAILS,
        company: job.company || '',
        position: job.jobTitle || '',
        targetLocation: job.location || ''
      }
    };
    setExtensionJob(null);
    setPendingJobData(newData);
  };


  const resetWorkspace = async () => {
    await resetWorkspaceContent();
    setShowConfirmReset(false);
  };

  const activeWorkspaces = appMode === 'initial' ? [] : workspaces.filter(w => w.mode === appMode);
  const activeId = appMode === 'initial' ? null : activeIds[appMode];
  
  const tailorWorkspaces = workspaces.filter(w => w.mode === 'tailor');
  const reachedMaxTailorWorkspaces = tailorWorkspaces.length >= 10;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="min-h-screen lg:h-screen lg:overflow-hidden w-full flex flex-col font-sans bg-slate-50 dark:bg-slate-950 text-slate-700 dark:text-slate-300 selection:bg-emerald-500/30">
      <CustomToaster />
      
      <LayoutHeader 
        status={'ready'} 
        resetWorkspace={() => setShowConfirmReset(true)}
        onHome={handleGoHome}
        appMode={appMode}
      />

      {appMode !== 'initial' && (
        <div className="flex bg-slate-100 dark:bg-slate-900 border-b border-slate-300 dark:border-slate-800">
           <button onClick={handleGoHome} className="flex px-4 items-center shrink-0 border-r border-slate-300 dark:border-slate-800 hover:bg-slate-200 dark:hover:bg-slate-800/80 group cursor-pointer" title="Back to Options">
               <ArrowLeft className="w-4 h-4 text-slate-500 group-hover:text-emerald-600 transition-colors" />
           </button>
           <Tabs
             workspaces={activeWorkspaces as any}
             activeId={activeId as string}
             onSelect={(id) => persistActiveIds({ ...activeIds, [appMode]: id })}
             onAdd={handleAddWorkspace}
             onClose={(id, e) => { e.stopPropagation(); handleCloseWorkspace(id); }}
             onRename={handleRenameWorkspace}
           />
        </div>
      )}
      
      <main className="flex-1 flex flex-col min-h-0 relative">
        <AnimatePresence mode="wait">
          {appMode === 'initial' && (
            <motion.div key="initial" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.2 }} className="flex-1 flex items-center justify-center p-6 bg-slate-50/50 dark:bg-slate-950 h-full w-full absolute inset-0 z-10">
               <div className="max-w-2xl w-full grid grid-cols-1 md:grid-cols-2 gap-6">
                  <motion.button initial="normal" whileHover="hover" onClick={() => handleSelectMode('job_search')} className="relative flex flex-col text-left items-start p-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl hover:border-emerald-500 hover:shadow-md group cursor-pointer">
                     <div className="w-12 h-12 bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110">
                       <AnimatedCompass className="w-6 h-6" />
                     </div>
                     <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-2">Find Matching Jobs</h2>
                     <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">Let AI search the web for active job listings that match your skills, location, and salary goals.</p>
                  </motion.button>
                  <motion.button initial="normal" whileHover="hover" onClick={() => handleSelectMode('tailor')} className="flex flex-col text-left items-start p-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl hover:border-indigo-500 hover:shadow-md group cursor-pointer">
                     <div className="w-12 h-12 bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-400 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110">
                       <AnimatedFileSearch className="w-6 h-6" />
                     </div>
                     <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-2">Resume Analyzing</h2>
                     <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">Already found a job? Paste the description or URL to instantly tailor your resume and cover letter.</p>
                  </motion.button>
               </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {activeWorkspaces.map(w => (
            <WorkspaceInstance
              key={w.id}
              id={w.id}
              isActive={activeId === w.id}
              mode={w.mode}
              recordUsage={recordUsage}
              fireWebhook={fireWebhook}
              onImportJob={(data) => setPendingJobData(data)}
              onModeChange={(newMode) => {
                if (newMode === 'initial') handleGoHome();
                else handleSelectMode(newMode as any);
              }}
            />
        ))}
      </main>
      
      <LayoutFooter />
      <ResetModal isOpen={showConfirmReset} onClose={() => setShowConfirmReset(false)} onConfirm={resetWorkspace} />
      <JobImportModal
        job={extensionJob}
        isOpen={!!extensionJob}
        onClose={() => setExtensionJob(null)}
        onTailor={handleTailorFromExtension}
      />
      
      <ResponsiveOverlay isOpen={!!pendingJobData} onClose={() => setPendingJobData(null)} variant="modal" className="sm:max-w-md w-full">
        <div className="p-6">
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-2">Select Workspace</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
            Where would you like to tailor your resume for <strong>{pendingJobData?.targetDetails?.company || 'this role'}</strong>?
          </p>

          <div className="space-y-3 mb-6 max-h-[40vh] overflow-y-auto">
            {!reachedMaxTailorWorkspaces && (
              <button
                onClick={() => {
                  importJob(pendingJobData);
                  setPendingJobData(null);
                }}
                className="w-full flex flex-col items-start gap-3 p-4 rounded-xl border border-dashed border-emerald-300 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10 hover:bg-emerald-100 dark:hover:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-medium transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-800/50 flex items-center justify-center shrink-0">
                    <Plus className="w-4 h-4" />
                  </div>
                  <span>Create New Workspace</span>
                </div>
              </button>
            )}

            {reachedMaxTailorWorkspaces && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 text-amber-700 dark:text-amber-400 text-sm rounded-lg">
                You have reached the maximum of 10 workspaces. Please overwrite an existing one or delete open workspaces from the tabs above.
              </div>
            )}

            {tailorWorkspaces.map(w => (
              <button
                key={w.id}
                onClick={() => {
                  importJobToExisting(pendingJobData, w.id);
                  setPendingJobData(null);
                }}
                className="w-full flex flex-col items-start gap-3 p-4 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-emerald-500 hover:shadow-sm bg-white dark:bg-slate-900 transition-all cursor-pointer group"
              >
                <div className="flex items-center gap-3 w-full text-left">
                  <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 shrink-0">
                    <AnimatedFileSearch className="w-4 h-4" />
                  </div>
                  <span className="font-medium text-slate-700 dark:text-slate-300 truncate">
                    {w.name}
                  </span>
                </div>
                <div className="pl-11 w-full flex justify-start">
                  <span className="text-xs text-slate-400 group-hover:text-emerald-500 px-2 py-1 rounded bg-slate-50 dark:bg-slate-800 group-hover:bg-emerald-50 dark:group-hover:bg-emerald-900/30">
                    Overwrite
                  </span>
                </div>
              </button>
            ))}
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-100 dark:border-slate-800">
            <button
              onClick={() => setPendingJobData(null)}
              className="px-4 py-2 rounded-lg font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      </ResponsiveOverlay>
    </motion.div>
  );
}


